import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { authGet, authPost, authPut, authDelete } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../components/ui/alert-dialog';
import { Progress } from '../components/ui/progress';
import { ChartPie, Plus, PencilSimple, Trash, Warning, CheckCircle, Info } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

const CATEGORIES = [
  { name: 'Food', icon: '🍔', color: '#F59E0B' },
  { name: 'Transport', icon: '🚌', color: '#3B82F6' },
  { name: 'Rent', icon: '🏠', color: '#8B5CF6' },
  { name: 'Entertainment', icon: '🎮', color: '#EC4899' },
  { name: 'Shopping', icon: '🛍️', color: '#10B981' },
  { name: 'Subscriptions', icon: '📱', color: '#6366F1' },
  { name: 'Bills & Utilities', icon: '💡', color: '#F97316' },
  { name: 'Other', icon: '📦', color: '#9CA3AF' }
];

export default function Budget() {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [form, setForm] = useState({
    category: '',
    amount: ''
  });

  // Monthly total budget
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [showMonthlyDialog, setShowMonthlyDialog] = useState(false);

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const fetchData = useCallback(async () => {
    try {
      const [budgetsRes, comparisonRes, settingsRes] = await Promise.all([
        authGet('/api/budgets'),
        authGet('/api/budget-comparison'),
        authGet('/api/settings')
      ]);
      
      setBudgets(budgetsRes.data);
      setComparison(comparisonRes.data);
      setMonthlyBudget(settingsRes.data.onboarding_data?.monthly_budget || '');
    } catch (e) {
      console.error('Fetch budget error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setForm({ category: '', amount: '' });
    setEditingBudget(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (budget) => {
    setEditingBudget(budget);
    setForm({
      category: budget.category,
      amount: budget.amount.toString()
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!form.category) {
      toast.error('Please select a category');
      return;
    }
    setSubmitting(true);

    try {
      const payload = {
        category: form.category,
        amount: parseFloat(form.amount),
        period: 'monthly'
      };

      if (editingBudget) {
        await authPut(`/api/budgets/${editingBudget.id}`, payload);
        toast.success('Budget updated');
      } else {
        await authPost('/api/budgets', payload);
        toast.success('Budget created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      console.error('Save budget error:', e);
      const msg = e.response?.data?.detail || e.message || 'Failed to save budget';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!id) {
      toast.error('Cannot delete: Invalid budget ID');
      return;
    }
    
    const originalBudgets = [...budgets];
    const originalComparison = [...comparison];
    
    setBudgets(prev => prev.filter(b => b.id !== id));
    setComparison(prev => prev.filter(c => c.id !== id));
    setDeleteTarget(null);
    
    try {
      await authDelete(`/api/budgets/${id}`);
      console.log('Budget deleted:', id);
      toast.success('Budget deleted');
      fetchData();
    } catch (e) {
      console.error('Delete budget error:', e.response?.data || e.message);
      setBudgets(originalBudgets);
      setComparison(originalComparison);
      const msg = e.response?.data?.detail || e.message || 'Failed to delete budget';
      toast.error(`Delete failed: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
    }
  };

  const handleSaveMonthlyBudget = async () => {
    try {
      await authPut('/api/settings/monthly-budget', { monthly_budget: parseFloat(monthlyBudget) || 0 });
      setShowMonthlyDialog(false);
      fetchData();
    } catch (e) {
      console.error('Save monthly budget error:', e);
    }
  };

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate budget status
  const getBudgetStatus = (percentage) => {
    if (percentage >= 100) return { status: 'over', color: '#EF4444', bgColor: 'bg-[#EF4444]/20', borderColor: 'border-[#EF4444]/30' };
    if (percentage >= 80) return { status: 'warning', color: '#F59E0B', bgColor: 'bg-[#F59E0B]/20', borderColor: 'border-[#F59E0B]/30' };
    return { status: 'good', color: '#10B981', bgColor: 'bg-[#10B981]/20', borderColor: 'border-[#10B981]/30' };
  };

  const availableCategories = CATEGORIES.filter(
    cat => !budgets.some(b => b.category === cat.name)
  );

  // Totals
  const totals = useMemo(() => {
    const totalBudget = budgets.reduce((sum, b) => sum + b.amount, 0);
    const totalSpent = comparison.reduce((sum, c) => sum + c.spent, 0);
    const overBudgetCount = comparison.filter(c => c.over_budget).length;
    const warningCount = comparison.filter(c => c.percentage >= 80 && c.percentage < 100).length;
    return { totalBudget, totalSpent, overBudgetCount, warningCount, remaining: totalBudget - totalSpent };
  }, [budgets, comparison]);

  const overallPercentage = totals.totalBudget > 0 ? (totals.totalSpent / totals.totalBudget) * 100 : 0;
  const overallStatus = getBudgetStatus(overallPercentage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="budget-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Budget Planner</h1>
          <p className="text-[#9CA3AF] mt-1">Set limits and track your spending</p>
        </div>
        
        <Button 
          onClick={openAddDialog}
          disabled={availableCategories.length === 0}
          className="btn-primary flex items-center gap-2"
          data-testid="add-budget-btn"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Add Budget</span>
        </Button>
      </div>

      {/* Overall Budget Summary */}
      <div className={`card-default p-6 border ${overallStatus.borderColor}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="font-semibold text-lg">Monthly Overview</h3>
            <p className="text-sm text-[#9CA3AF]">Total budget across all categories</p>
          </div>
          {totals.overBudgetCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-full">
              <Warning size={16} className="text-[#EF4444]" />
              <span className="text-sm text-[#EF4444] font-medium">
                {totals.overBudgetCount} over budget
              </span>
            </div>
          )}
          {totals.warningCount > 0 && totals.overBudgetCount === 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-full">
              <Info size={16} className="text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">
                {totals.warningCount} close to limit
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-sm text-[#9CA3AF]">Total Budget</p>
            <p className="text-xl font-semibold text-[#F9FAFB] tabular-nums">
              {formatCurrency(totals.totalBudget)}
            </p>
          </div>
          <div>
            <p className="text-sm text-[#9CA3AF]">Spent</p>
            <p className="text-xl font-semibold tabular-nums" style={{ color: overallStatus.color }}>
              {formatCurrency(totals.totalSpent)}
            </p>
          </div>
          <div>
            <p className="text-sm text-[#9CA3AF]">Remaining</p>
            <p className={`text-xl font-semibold tabular-nums ${totals.remaining >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {formatCurrency(totals.remaining)}
            </p>
          </div>
          <div>
            <p className="text-sm text-[#9CA3AF]">Usage</p>
            <p className="text-xl font-semibold tabular-nums" style={{ color: overallStatus.color }}>
              {Math.round(overallPercentage)}%
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-xs text-[#9CA3AF]">
            <span>0%</span>
            <span>80%</span>
            <span>100%</span>
          </div>
          <div className="relative h-3 bg-[#1F2937] rounded-full overflow-hidden">
            {/* Warning zone marker */}
            <div className="absolute left-[80%] top-0 bottom-0 w-px bg-[#F59E0B]/50" />
            {/* Progress bar */}
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(overallPercentage, 100)}%`,
                backgroundColor: overallStatus.color
              }}
            />
          </div>
        </div>
      </div>

      {/* Category Budgets */}
      <div className="space-y-4" data-testid="budget-list">
        <h3 className="font-semibold text-lg">Category Budgets</h3>
        
        {comparison.length > 0 ? (
          <div className="grid gap-4">
            {comparison.map((item) => {
              const status = getBudgetStatus(item.percentage);
              const categoryInfo = CATEGORIES.find(c => c.name === item.category) || { icon: '📦', color: '#9CA3AF' };
              
              return (
                <div 
                  key={item.category} 
                  className={`card-default p-5 border ${status.borderColor} transition-all hover:border-opacity-60`}
                  data-testid={`budget-item-${item.category.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${status.bgColor}`}
                      >
                        {categoryInfo.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{item.category}</p>
                          {item.over_budget && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-[#EF4444]/20 text-[#EF4444] rounded-full">
                              Over budget
                            </span>
                          )}
                          {item.percentage >= 80 && item.percentage < 100 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">
                              Close to limit
                            </span>
                          )}
                          {item.percentage < 80 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-[#10B981]/20 text-[#10B981] rounded-full">
                              On track
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#9CA3AF]">
                          {item.remaining >= 0 
                            ? `${formatCurrency(item.remaining)} remaining`
                            : `${formatCurrency(Math.abs(item.remaining))} over`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold tabular-nums" style={{ color: status.color }}>
                          {formatCurrency(item.spent)}
                        </p>
                        <p className="text-sm text-[#9CA3AF]">of {formatCurrency(item.budget)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditDialog({ id: item.id, category: item.category, amount: item.budget })}
                          className="text-[#9CA3AF] hover:text-[#3B82F6] transition-colors p-1"
                          data-testid={`edit-budget-${item.category.toLowerCase()}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item.id)}
                          className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors p-1"
                          data-testid={`delete-budget-${item.category.toLowerCase()}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-[#9CA3AF]">
                      <span>{Math.round(item.percentage)}% used</span>
                      <span>{100 - Math.round(item.percentage)}% left</span>
                    </div>
                    <div className="relative h-2 bg-[#1F2937] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: status.color
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card-default p-8 text-center">
            <ChartPie size={48} className="text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#9CA3AF]">No budgets set yet</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Add a budget to start tracking your spending limits</p>
          </div>
        )}
      </div>

      {/* Quick Add Budgets */}
      {availableCategories.length > 0 && (
        <div className="card-default p-5">
          <h3 className="font-semibold mb-4">Quick Add</h3>
          <div className="flex flex-wrap gap-2">
            {availableCategories.map(cat => (
              <button
                key={cat.name}
                onClick={() => {
                  setForm({ category: cat.name, amount: '' });
                  setDialogOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#1F2937] hover:bg-[#374151] rounded-xl transition-colors text-sm"
              >
                <span>{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Budget Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#F9FAFB]">
              {editingBudget ? 'Edit Budget' : 'Set Budget'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {!editingBudget && (
              <div className="space-y-2">
                <Label className="text-[#F9FAFB]">Category</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableCategories.map(cat => (
                    <button
                      key={cat.name}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, category: cat.name }))}
                      className={`flex items-center gap-2 p-3 rounded-xl text-sm text-left transition-all ${
                        form.category === cat.name
                          ? 'bg-[#3B82F6]/20 border border-[#3B82F6]/40 text-[#F9FAFB]'
                          : 'bg-[#1F2937] border border-transparent text-[#9CA3AF] hover:border-white/10'
                      }`}
                    >
                      <span>{cat.icon}</span>
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {editingBudget && (
              <div className="flex items-center gap-3 p-3 bg-[#1F2937] rounded-xl">
                <span className="text-xl">{CATEGORIES.find(c => c.name === form.category)?.icon || '📦'}</span>
                <span className="font-medium">{form.category}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Monthly Budget</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="input-default flex-1"
                  required
                  data-testid="budget-amount"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                onClick={() => { setDialogOpen(false); resetForm(); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !form.amount || !form.category}
                className="btn-primary flex-1"
                data-testid="save-budget-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingBudget ? 'Update' : 'Save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#111827] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F9FAFB]">Delete budget?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This will permanently remove this category budget.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1F2937] border-white/10 text-[#F9FAFB] hover:bg-[#374151]" data-testid="cancel-delete-budget">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#EF4444] text-white hover:bg-[#DC2626]" onClick={() => handleDelete(deleteTarget)} data-testid="confirm-delete-budget">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
